import React from 'react';
import cloneDeep from 'lodash.clonedeep';
import { Button } from '@progress/kendo-react-buttons';

import { TradeCard } from '.';
import {
  PostTradeActionAsync,
  PostTradeOfferOrRespondAsync
} from '../../../../../../services/trade';
import { ItemAchievement } from '../inventory';
import { putToStore } from '../../../../../../utils/indexedDB';
import { PlayerContext } from '../../../../../../contexts/player';
import { TradeStateContext } from '../../../../../../contexts/trade';
import { GameDocumentContext } from '../../../../../../contexts/game-document';
import { GetItemAchievement } from '../../../../../../utils/game-engine/inventory';
import { generateTitleById } from '../../../../../../utils/game-document/display-languages';
import { DisplayLanguageContext } from '../../../../../../contexts/display-languages';

interface OfferedItemsProps {
  playerItems: ItemAchievement[];
  setPlayerItems: React.Dispatch<React.SetStateAction<ItemAchievement[]>>;
  showModal: React.Dispatch<React.SetStateAction<ItemAchievement | undefined>>;
}

const OfferedItems: React.FC<OfferedItemsProps> = ({
  playerItems,
  showModal,
  setPlayerItems
}) => {
  const [gameDocument] = React.useContext(GameDocumentContext);
  const [player] = React.useContext(PlayerContext);
  const [trade, setTrade] = React.useContext(TradeStateContext);
  const [displayLanguageContext] = React.useContext(DisplayLanguageContext);
  const [offeredItems, setOfferedItems] = React.useState<ItemAchievement[]>([]);
  const playerCode = player.playerState?.code ?? '';

  const tradeRole = playerCode === trade.offerPlayerCode ? 'offer' : 'respond';
  const tradeStatus =
    tradeRole === 'offer' ? trade.offerPlayerStatus : trade.requestPlayerStatus;

  const updateOfferedItems = (
    items: {
      itemId: string;
      quantity: number;
      isOffered: boolean;
    }[]
  ) => {
    setTrade((prev) => {
      const result = {
        ...prev,
        offeredItems: items
      };
      putToStore('trade', result);
      return result;
    });
  };

  const onReduceItem = (item: ItemAchievement) => {
    if (item.quantity < 1) return;

    const offeredIndex = offeredItems.findIndex((i) => i.id === item.id);
    if (offeredIndex !== -1) {
      const offered = cloneDeep(offeredItems);
      const selectedItem = cloneDeep(offeredItems[offeredIndex]);
      selectedItem.quantity--;
      offered.splice(offeredIndex, 1, selectedItem);
      const result = offered.filter((i) => i.quantity > 0);
      setOfferedItems(offered.filter((i) => i.quantity > 0));
      updateOfferedItems(
        result.map((i) => ({
          itemId: i.id,
          quantity: i.quantity,
          isOffered: true
        }))
      );

      const selectedIndex = playerItems.findIndex((i) => i.id === item.id);
      if (selectedIndex !== -1) {
        const items = cloneDeep(playerItems);
        const selectedItem = cloneDeep(playerItems[selectedIndex]);
        selectedItem.quantity++;
        items.splice(selectedIndex, 1, selectedItem);
        setPlayerItems(items);
      }
    }
  };

  const onOfferItem = async () => {
    try {
      const items = offeredItems.map((i) => ({
        itemId: i.id,
        quantity: i.quantity,
        isOffered: true
      }));
      await PostTradeOfferOrRespondAsync(
        trade.gameCode,
        String(trade.id),
        tradeRole,
        items
      );
    } catch (error) {
      console.log(error, '>>>>error when offer items');
    }
  };

  const onCounterItem = async () => {
    try {
      await PostTradeActionAsync(
        trade.gameCode,
        String(trade.id),
        tradeRole,
        'counter'
      );
    } catch (error) {
      console.log(error, '>>>>error when counter items');
    }
  };

  React.useEffect(() => {
    if (gameDocument.gameDocument && trade.offeredItems) {
      setOfferedItems(
        trade.offeredItems.map((i) =>
          GetItemAchievement(
            gameDocument.gameDocument!,
            player.playerState?.language.name ?? '',
            i.itemId,
            i.quantity
          )
        )
      );
    }
  }, [
    trade.offeredItems,
    gameDocument.gameDocument,
    player.playerState?.language.name
  ]);

  return (
    <>
      <div className={'d-flex justify-content-between align-items-center'}>
        <span>
          {generateTitleById(
            'c3d4e5f6-a7b8-c9d0-e1f2-a3b4c5d6e7f8',
            gameDocument,
            displayLanguageContext.displayLanguageSelected.resources!,
            'game'
          ) || 'Your offer'}
        </span>
        <Button
          themeColor={'primary'}
          onClick={tradeStatus === 'Draft' ? onOfferItem : onCounterItem}>
          {tradeStatus === 'Draft'
            ? generateTitleById(
                'd4e5f6a7-b8c9-d0e1-f2a3-b4c5d6e7f8a9',
                gameDocument,
                displayLanguageContext.displayLanguageSelected.resources!,
                'game'
              ) || 'Make offer'
            : generateTitleById(
                'e5f6a7b8-c9d0-e1f2-a3b4-c5d6e7f8a9b0',
                gameDocument,
                displayLanguageContext.displayLanguageSelected.resources!,
                'game'
              ) || 'Counter offer'}
        </Button>
      </div>
      <div
        className={'d-flex flex-grow-1 flex-column gap-2'}
        style={{
          borderRadius: '12px',
          border: '1px solid black',
          padding: '12px',
          height: '100%',
          overflow: 'hidden'
        }}>
        <div className={'d-flex justify-content-between'}>
          <span>
            {generateTitleById(
              'f6a7b8c9-d0e1-f2a3-b4c5-d6e7f8a9b0c1',
              gameDocument,
              displayLanguageContext.displayLanguageSelected.resources!,
              'game'
            ) || 'Tap to remove'}
          </span>
          <span>
            {tradeStatus === 'Draft'
              ? generateTitleById(
                  '0a1b2c3d-4e5f-6a7b-8c9d-0e1f2a3b4c5d',
                  gameDocument,
                  displayLanguageContext.displayLanguageSelected.resources!,
                  'game'
                ) || 'Draft'
              : generateTitleById(
                  '1b2c3d4e-5f6a-7b8c-9d0e-1f2a3b4c5d6e',
                  gameDocument,
                  displayLanguageContext.displayLanguageSelected.resources!,
                  'game'
                ) || 'Pending'}
          </span>
        </div>
        <div className={'d-flex flex-wrap gap-2 flex-grow-1 overflow-auto'}>
          {offeredItems
            .filter((item) => !item.hideInGame)
            .map((item) => (
              <TradeCard
                key={item.id}
                item={item}
                isLocked={tradeStatus !== 'Draft'}
                clickCard={() => onReduceItem(item)}
                showModal={() => showModal(item)}
              />
            ))}
        </div>
      </div>
    </>
  );
};

export default OfferedItems;
